import styled from 'styled-components';

import DonutChart from 'components/shared/charts/DonutChart';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { DenialCategorySlice } from '../constants';

// Donut height (px) — reserved by MetricDetailLayout's placeholder so the card doesn't collapse and
// jump on load.
export const DENIAL_BREAKDOWN_CHART_HEIGHT = 200;

type Props = {
  categoryRows: DenialCategorySlice[];
  totalDenied: number;
};

// The Denied drill's "Denial Breakdown" donut (OLIO-2897): denials split by category. A thin,
// number-only donut (Service Refusals style) with a color-key legend beneath it, and a
// share-of-total legend (pct + count, divider per row) alongside.
export default function InboundDenialBreakdownChart({ categoryRows, totalDenied }: Props) {
  // Order and colors come from denialCategorySlices (constants), so the chart just draws them.
  const chartData = categoryRows.map((row) => ({ name: row.category, y: row.count, color: row.color }));

  return (
    <ChartLegendRow>
      <DonutColumn>
        <DonutChart
          data={chartData}
          centerValue={totalDenied}
          centerLabel=''
          config={{
            chart: { height: 200, width: 200 },
            // Number only in the center (no label), like the Service Refusals donut.
            title: {
              text: `<div style="text-align:center;pointer-events:none;"><b style="font-size:24px;font-weight:700;color:${colors.black100};">${totalDenied}</b></div>`,
              y: 16,
            },
            // We render our own legend alongside; keep the Highcharts legend off.
            legend: { enabled: false },
            // Same base tooltip (name: count (pct%)) as the landing donut, but render it in-chart:
            // the base's `outside: true` appends to document.body, which sits *behind* the slideout
            // overlay and never shows. Our donut column has overflow:visible so it won't clip.
            tooltip: { outside: false },
            plotOptions: { pie: { size: 190, innerSize: '72%', center: ['50%', '50%'] } },
          }}
        />
      </DonutColumn>
      <ShareLegend>
        {chartData.map((item) => {
          const pct = totalDenied > 0 ? Math.round((item.y / totalDenied) * 100) : 0;
          return (
            <ShareItem key={item.name}>
              <Dot $color={item.color} />
              <ShareName>{item.name}</ShareName>
              <ShareValue>
                {pct}% <ShareCount>({item.y})</ShareCount>
              </ShareValue>
            </ShareItem>
          );
        })}
      </ShareLegend>
    </ChartLegendRow>
  );
}

const ChartLegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(0px, 3vw, 40px);
  padding: 0 clamp(16px, 4vw, 48px);

  @media (max-width: 800px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    padding: 0;
  }
`;

const DonutColumn = styled.div`
  flex: 0 0 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  /* Highcharts injects inline flex:1 1 0 and overflow:hidden on its wrapper, which collapse to 0
     height inside this column and hide the chart — pin it to the chart's fixed size instead. */
  > :first-child {
    flex: none !important;
    width: 200px;
    height: 200px;
    overflow: visible !important;
  }

  @media (max-width: 800px) {
    flex: none;
    align-self: center;
  }
`;

const ShareLegend = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ShareItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
`;

const ShareName = styled(BodySmall)`
  flex: 1;
  font-weight: var(--font-weight-medium);
  color: ${({ theme }) => theme.colors.black100};
`;

const ShareValue = styled(BodySmall)`
  white-space: nowrap;
  font-weight: var(--font-weight-medium);
  color: ${({ theme }) => theme.colors.black100};
`;

const ShareCount = styled.span`
  font-weight: var(--font-weight-regular);
  color: ${({ theme }) => theme.colors.black50};
`;

const Dot = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;
